import { Pipe, type PipeTransform } from '@angular/core';
import { formatBytes, formatDate, getValueAtPath, resolveFileType, type DocumentItem } from '@document-explorer/core';
import type { ColumnDef } from './types.js';

/**
 * Renders one column's cell text. Mirrors the React package's `cellContent`
 * exactly — the two are checked against each other by the parity test suite.
 */
@Pipe({ name: 'deCellValue', standalone: true, pure: true })
export class DeCellValuePipe implements PipeTransform {
  transform(item: DocumentItem, column: ColumnDef, locale?: string): string {
    switch (column.key) {
      case 'name':
        return item.name;
      case 'type':
        return resolveFileType(item).label;
      case 'size':
        return item.type === 'folder' ? '' : formatBytes(item.size, locale);
      case 'createdAt':
        return formatDate(item.createdAt, locale);
      case 'updatedAt':
        return formatDate(item.updatedAt, locale);
      default: {
        const value = getValueAtPath(item, column.key);
        if (value == null) return '';
        if (value instanceof Date) return formatDate(value, locale);
        if (typeof value === 'boolean') return value ? 'Yes' : 'No';
        if (typeof value === 'object') return JSON.stringify(value);
        return String(value);
      }
    }
  }
}

/** Grid-card subtitle: `PDF · 2.3 MB`, or just `Folder`. */
@Pipe({ name: 'deCardMeta', standalone: true, pure: true })
export class DeCardMetaPipe implements PipeTransform {
  transform(item: DocumentItem, locale?: string): string {
    if (item.type === 'folder') return 'Folder';
    return [resolveFileType(item).label, formatBytes(item.size, locale)].filter(Boolean).join(' · ');
  }
}

/** Which actions apply to a given row. */
@Pipe({ name: 'deVisibleActions', standalone: true, pure: true })
export class DeVisibleActionsPipe implements PipeTransform {
  transform<T extends { visible?: (item: DocumentItem) => boolean }>(actions: T[], item: DocumentItem): T[] {
    return actions.filter((a) => (a.visible ? a.visible(item) : true));
  }
}
